import { l as getFocusableElements } from "./focus-DaIphPOd.mjs";
import { Keys } from "./utils/index.mjs";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import * as Y from "yjs";
import { z } from "zod";
//#region hooks/useCanvasViewport.ts
const MIN_ZOOM = .1;
const MAX_ZOOM = 3;
function clampZoom(z) {
	return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}
/**
* Canvas pan/zoom state management
*
* Pan: middle-click or left-click drag updates offset.
* Zoom: Ctrl/Cmd + wheel with focal-point zoom toward cursor.
* Zoom range: 0.1 - 3.0
*/
function useCanvasViewport() {
	const [offset, setOffset] = useState({
		x: 0,
		y: 0
	});
	const [zoom, setZoomState] = useState(1);
	const [isPanning, setIsPanning] = useState(false);
	const panStartRef = useRef(null);
	const offsetAtPanStartRef = useRef({
		x: 0,
		y: 0
	});
	const onMouseDown = useCallback((e) => {
		if (e.button === 1 || e.button === 0) {
			panStartRef.current = {
				x: e.clientX,
				y: e.clientY
			};
			offsetAtPanStartRef.current = {
				x: offset.x,
				y: offset.y
			};
			setIsPanning(true);
		}
	}, [offset]);
	const onMouseMove = useCallback((e) => {
		if (!panStartRef.current) return;
		const dx = e.clientX - panStartRef.current.x;
		const dy = e.clientY - panStartRef.current.y;
		setOffset({
			x: offsetAtPanStartRef.current.x + dx,
			y: offsetAtPanStartRef.current.y + dy
		});
	}, []);
	const onMouseUp = useCallback(() => {
		panStartRef.current = null;
		setIsPanning(false);
	}, []);
	const onWheel = useCallback((e) => {
		if (!e.ctrlKey && !e.metaKey) return;
		e.preventDefault();
		const zoomFactor = e.deltaY < 0 ? 1.1 : .9;
		setZoomState((prevZoom) => {
			const newZoom = clampZoom(prevZoom * zoomFactor);
			const ratio = newZoom / prevZoom;
			const cursorX = e.clientX;
			const cursorY = e.clientY;
			setOffset((prevOffset) => ({
				x: cursorX - (cursorX - prevOffset.x) * ratio,
				y: cursorY - (cursorY - prevOffset.y) * ratio
			}));
			return newZoom;
		});
	}, []);
	const screenToWorld = useCallback((screenX, screenY) => ({
		x: (screenX - offset.x) / zoom,
		y: (screenY - offset.y) / zoom
	}), [offset, zoom]);
	const resetView = useCallback(() => {
		setOffset({
			x: 0,
			y: 0
		});
		setZoomState(1);
	}, []);
	const setZoom = useCallback((z) => {
		setZoomState(clampZoom(z));
	}, []);
	return {
		offset,
		zoom,
		isPanning,
		handlers: {
			onMouseDown,
			onMouseMove,
			onMouseUp,
			onWheel
		},
		screenToWorld,
		resetView,
		setZoom
	};
}
//#endregion
//#region hooks/useCollaboration.ts
/**
* React hook for real-time collaboration via Yjs and a generic RPC adapter.
*
* Manages Y.Doc synchronization, presence updates, and collaborator state.
* Accepts an IRpcAdapter for transport-agnostic RPC communication.
*/
/**
* Throttle function that limits how often a function can be called.
* Uses trailing edge - last call within the window is executed.
*/
function throttle(fn, limit) {
	let lastCall = 0;
	let timeoutId = null;
	let lastArgs = null;
	return (...args) => {
		const now = Date.now();
		lastArgs = args;
		if (now - lastCall >= limit) {
			lastCall = now;
			fn(...args);
		} else if (timeoutId === null) timeoutId = setTimeout(() => {
			lastCall = Date.now();
			timeoutId = null;
			if (lastArgs !== null) fn(...lastArgs);
		}, limit - (now - lastCall));
	};
}
function base64ToUint8Array(str) {
	const binary = atob(str);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
	return bytes;
}
function uint8ArrayToBase64(arr) {
	const binary = Array.from(arr).map((b) => String.fromCharCode(b)).join("");
	return btoa(binary);
}
function useCollaboration(roomId, options, rpc) {
	const [collaborators, setCollaborators] = useState([]);
	const [isConnected, setIsConnected] = useState(false);
	const docRef = useRef(new Y.Doc());
	const roomIdRef = useRef(null);
	const handleJoined = useCallback((data) => {
		const event = data;
		if (event.roomId !== roomIdRef.current) return;
		if (event.collaborator.id === options.userId) return;
		setCollaborators((prev) => {
			if (prev.some((c) => c.id === event.collaborator.id)) return prev;
			return [...prev, event.collaborator];
		});
	}, [options.userId]);
	const handleLeft = useCallback((data) => {
		const event = data;
		if (event.roomId !== roomIdRef.current) return;
		setCollaborators((prev) => prev.filter((c) => c.id !== event.collaboratorId));
	}, []);
	const handlePresence = useCallback((data) => {
		const event = data;
		if (event.roomId !== roomIdRef.current) return;
		if (event.collaborator.id === options.userId) return;
		setCollaborators((prev) => prev.map((c) => c.id === event.collaborator.id ? event.collaborator : c));
	}, [options.userId]);
	const handleYjsUpdate = useCallback((data) => {
		const event = data;
		if (event.roomId !== roomIdRef.current) return;
		const update = base64ToUint8Array(event.update);
		Y.applyUpdate(docRef.current, update, "remote");
	}, []);
	useEffect(() => {
		const unsubJoined = rpc.subscribe("collab.joined", handleJoined);
		const unsubLeft = rpc.subscribe("collab.left", handleLeft);
		const unsubPresence = rpc.subscribe("collab.presence", handlePresence);
		const unsubUpdate = rpc.subscribe("collab.update", handleYjsUpdate);
		return () => {
			unsubJoined();
			unsubLeft();
			unsubPresence();
			unsubUpdate();
		};
	}, [
		rpc,
		handleJoined,
		handleLeft,
		handlePresence,
		handleYjsUpdate
	]);
	useEffect(() => {
		const currentRoomId = roomIdRef.current;
		if (currentRoomId !== null && currentRoomId !== roomId) {
			rpc.call("collab.leave", {
				roomId: currentRoomId,
				collaboratorId: options.userId
			}).catch(() => {});
			setIsConnected(false);
			setCollaborators([]);
		}
		if (roomId !== null) {
			roomIdRef.current = roomId;
			rpc.call("collab.join", {
				roomId,
				collaborator: {
					id: options.userId,
					name: options.userName,
					color: options.userColor,
					cursor: null,
					selectedNodeId: null
				}
			}).then((result) => {
				if (result.state !== null) {
					const state = base64ToUint8Array(result.state);
					Y.applyUpdate(docRef.current, state, "remote");
				}
				setCollaborators(result.collaborators.filter((c) => c.id !== options.userId));
				setIsConnected(true);
			}).catch((err) => {
				console.error("[useCollaboration] Failed to join room:", err);
				setIsConnected(false);
			});
		} else roomIdRef.current = null;
		return () => {
			if (roomIdRef.current !== null) rpc.call("collab.leave", {
				roomId: roomIdRef.current,
				collaboratorId: options.userId
			}).catch(() => {});
		};
	}, [
		roomId,
		options.userId,
		options.userName,
		options.userColor,
		rpc
	]);
	useEffect(() => {
		const doc = docRef.current;
		const handleDocUpdate = (update, origin) => {
			if (origin === "remote") return;
			if (roomIdRef.current === null) return;
			const base64 = uint8ArrayToBase64(update);
			rpc.call("collab.sync", {
				roomId: roomIdRef.current,
				update: base64
			}).catch((err) => {
				console.error("[useCollaboration] Failed to sync update:", err);
			});
		};
		doc.on("update", handleDocUpdate);
		return () => doc.off("update", handleDocUpdate);
	}, [rpc]);
	const sendCursorThrottled = useCallback(throttle((cursor) => {
		if (roomIdRef.current === null) return;
		rpc.call("collab.cursor", {
			roomId: roomIdRef.current,
			collaboratorId: options.userId,
			cursor,
			selectedNodeId: null
		}).catch(() => {});
	}, 50), [options.userId, rpc]);
	const sendCursor = useCallback((cursor) => {
		sendCursorThrottled(cursor);
	}, [sendCursorThrottled]);
	const sendSelection = useCallback((nodeId) => {
		if (roomIdRef.current === null) return;
		rpc.call("collab.cursor", {
			roomId: roomIdRef.current,
			collaboratorId: options.userId,
			cursor: null,
			selectedNodeId: nodeId
		}).catch(() => {});
	}, [options.userId, rpc]);
	return {
		doc: docRef.current,
		collaborators,
		isConnected,
		sendCursor,
		sendSelection
	};
}
//#endregion
//#region hooks/useDocSync.ts
/**
* Generalized Yjs document synchronization hook.
*
* Provides configurable Y.Doc structure management, soft-locking with
* automatic expiry cleanup, and map sync/observe utilities. Composes
* useCollaboration for transport and connection state.
*/
const DEFAULT_LOCK_TIMEOUT_MS = 3e4;
const DEFAULT_CLEANUP_INTERVAL_MS = 5e3;
const LockEntrySchema = z.object({
	userId: z.string(),
	timestamp: z.number()
});
/**
* Safely parse unknown data as a LockEntry using Zod validation.
* Returns null if validation fails.
*/
function parseLockEntry(data) {
	const result = LockEntrySchema.safeParse(data);
	return result.success ? result.data : null;
}
function useDocSync(roomId, options) {
	const { userId, rpc, structures, lockMapName, lockTimeoutMs, cleanupIntervalMs } = options;
	const resolvedLockTimeout = lockTimeoutMs ?? DEFAULT_LOCK_TIMEOUT_MS;
	const resolvedCleanupInterval = cleanupIntervalMs ?? DEFAULT_CLEANUP_INTERVAL_MS;
	const { doc, isConnected } = useCollaboration(roomId, options, rpc);
	const structuresRef = useRef(/* @__PURE__ */ new Map());
	const lockMapRef = useRef(null);
	for (const structure of structures) if (!structuresRef.current.has(structure.name)) if (structure.type === "array") structuresRef.current.set(structure.name, doc.getArray(structure.name));
	else structuresRef.current.set(structure.name, doc.getMap(structure.name));
	if (lockMapName !== null && lockMapRef.current === null) lockMapRef.current = doc.getMap(lockMapName);
	const getArray = useCallback((name) => {
		const existing = structuresRef.current.get(name);
		if (existing !== void 0 && existing instanceof Y.Array) return existing;
		const arr = doc.getArray(name);
		structuresRef.current.set(name, arr);
		return arr;
	}, [doc]);
	const getMap = useCallback((name) => {
		const existing = structuresRef.current.get(name);
		if (existing !== void 0 && existing instanceof Y.Map) return existing;
		const map = doc.getMap(name);
		structuresRef.current.set(name, map);
		return map;
	}, [doc]);
	const isLockValid = useCallback((lock) => {
		if (lock === null) return false;
		return Date.now() - lock.timestamp < resolvedLockTimeout;
	}, [resolvedLockTimeout]);
	const getLockedBy = useCallback((key) => {
		if (lockMapRef.current === null) return null;
		const yLocks = lockMapRef.current;
		const lock = parseLockEntry(yLocks.get(key));
		if (lock === null) return null;
		if (!isLockValid(lock)) {
			doc.transact(() => {
				yLocks.delete(key);
			});
			return null;
		}
		return lock.userId;
	}, [isLockValid, doc]);
	const tryLock = useCallback((key) => {
		if (lockMapRef.current === null) return false;
		const yLocks = lockMapRef.current;
		const currentLock = parseLockEntry(yLocks.get(key));
		if (currentLock !== null && isLockValid(currentLock) && currentLock.userId !== userId) return false;
		doc.transact(() => {
			const newLock = {
				userId,
				timestamp: Date.now()
			};
			yLocks.set(key, newLock);
		});
		return true;
	}, [
		userId,
		isLockValid,
		doc
	]);
	const unlock = useCallback((key) => {
		if (lockMapRef.current === null) return;
		const yLocks = lockMapRef.current;
		const currentLock = parseLockEntry(yLocks.get(key));
		if (currentLock === null) return;
		if (currentLock.userId === userId) doc.transact(() => {
			yLocks.delete(key);
		});
	}, [userId, doc]);
	const syncMap = useCallback((name, data) => {
		const yMap = getMap(name);
		doc.transact(() => {
			for (const [k, v] of Object.entries(data)) yMap.set(k, v);
		});
	}, [doc, getMap]);
	const observeMap = useCallback((name, handler) => {
		const yMap = getMap(name);
		yMap.observe(handler);
		return () => yMap.unobserve(handler);
	}, [getMap]);
	useEffect(() => {
		if (lockMapRef.current === null) return;
		const yLocks = lockMapRef.current;
		const cleanupInterval = setInterval(() => {
			try {
				const now = Date.now();
				const keysToDelete = [];
				yLocks.forEach((value, key) => {
					const lock = parseLockEntry(value);
					if (lock !== null && now - lock.timestamp >= resolvedLockTimeout) keysToDelete.push(key);
				});
				if (keysToDelete.length > 0) doc.transact(() => {
					for (const key of keysToDelete) yLocks.delete(key);
				});
			} catch (error) {
				console.warn("Error cleaning up expired locks:", error);
			}
		}, resolvedCleanupInterval);
		return () => clearInterval(cleanupInterval);
	}, [
		doc,
		resolvedLockTimeout,
		resolvedCleanupInterval
	]);
	return {
		yDoc: doc,
		isConnected,
		tryLock,
		unlock,
		getLockedBy,
		syncMap,
		observeMap,
		getArray,
		getMap
	};
}
//#endregion
//#region hooks/useVisualViewport.ts
function getInitialState() {
	if (typeof window === "undefined") return {
		height: 0,
		width: 0,
		offsetTop: 0,
		scale: 1,
		isKeyboardVisible: false
	};
	const vv = window.visualViewport;
	return {
		height: vv !== null && vv !== void 0 ? vv.height : window.innerHeight,
		width: vv !== null && vv !== void 0 ? vv.width : window.innerWidth,
		offsetTop: 0,
		scale: 1,
		isKeyboardVisible: false
	};
}
function useVisualViewport() {
	const [state, setState] = useState(getInitialState);
	useEffect(() => {
		const vv = window.visualViewport;
		if (vv === null || vv === void 0) return;
		const update = () => {
			const isKeyboardVisible = vv.height < window.innerHeight * .75;
			setState({
				height: vv.height,
				width: vv.width,
				offsetTop: vv.offsetTop,
				scale: vv.scale,
				isKeyboardVisible
			});
		};
		update();
		vv.addEventListener("resize", update);
		vv.addEventListener("scroll", update);
		return () => {
			vv.removeEventListener("resize", update);
			vv.removeEventListener("scroll", update);
		};
	}, []);
	return state;
}
//#endregion
//#region hooks/useHaptic.ts
const DURATIONS = {
	light: 10,
	medium: 20,
	heavy: [
		20,
		10,
		20
	],
	selection: 5
};
/** Progressive-enhancement haptic feedback. Silent fallback on unsupported platforms. */
function useHaptic() {
	return (style) => {
		try {
			if ("vibrate" in navigator) navigator.vibrate(DURATIONS[style]);
		} catch {}
	};
}
//#endregion
//#region hooks/index.ts
/**
* EITELKEIT Hooks
* ═══════════════════════════════════════════════════════════════════
*/
/**
* Generate a unique ID for accessibility
*/
function useId$1(prefix = "e") {
	return `${prefix}-${useId()}`;
}
/**
* Manage controlled/uncontrolled state
*/
function useControllableState(controlledValue, defaultValue, onChange) {
	const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
	const isControlled = controlledValue !== null;
	return [isControlled ? controlledValue : uncontrolledValue, useCallback((nextValue) => {
		if (!isControlled) setUncontrolledValue(nextValue);
		onChange?.(nextValue);
	}, [isControlled, onChange])];
}
/**
* Focus trap for modals and overlays
*/
function useFocusTrap(active) {
	const containerRef = useRef(null);
	useEffect(() => {
		if (!active || !containerRef.current) return;
		const container = containerRef.current;
		const focusableElements = getFocusableElements(container);
		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];
		firstElement?.focus();
		const handleKeyDown = (event) => {
			if (event.key !== "Tab") return;
			if (event.shiftKey) {
				if (document.activeElement === firstElement) {
					event.preventDefault();
					lastElement?.focus();
				}
			} else if (document.activeElement === lastElement) {
				event.preventDefault();
				firstElement?.focus();
			}
		};
		container.addEventListener("keydown", handleKeyDown);
		return () => container.removeEventListener("keydown", handleKeyDown);
	}, [active]);
	return containerRef;
}
/**
* Click outside detection
*/
function useClickOutside(callback, active = true) {
	const ref = useRef(null);
	useEffect(() => {
		if (!active) return;
		const handleClick = (event) => {
			if (ref.current && !ref.current.contains(event.target)) callback();
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [callback, active]);
	return ref;
}
/**
* Escape key handler
*/
function useEscapeKey(callback, active = true) {
	useEffect(() => {
		if (!active) return;
		const handleKeyDown = (event) => {
			if (event.key === "Escape") callback();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [callback, active]);
}
/**
* Lock body scroll when overlay is open
*/
function useScrollLock(active) {
	useEffect(() => {
		if (!active) return;
		const originalStyle = window.getComputedStyle(document.body).overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = originalStyle;
		};
	}, [active]);
}
/**
* Keyboard navigation for lists
*/
function useKeyboardNavigation(itemCount, options = {}) {
	const { loop = true, onSelect, onEscape } = options;
	const [activeIndex, setActiveIndex] = useState(-1);
	return {
		activeIndex,
		setActiveIndex,
		handleKeyDown: useCallback((event) => {
			switch (event.key) {
				case Keys.ArrowDown:
					event.preventDefault();
					setActiveIndex((prev) => {
						if (prev >= itemCount - 1) return loop ? 0 : prev;
						return prev + 1;
					});
					break;
				case Keys.ArrowUp:
					event.preventDefault();
					setActiveIndex((prev) => {
						if (prev <= 0) return loop ? itemCount - 1 : 0;
						return prev - 1;
					});
					break;
				case Keys.Enter:
				case Keys.Space:
					event.preventDefault();
					if (activeIndex >= 0) onSelect?.(activeIndex);
					break;
				case Keys.Escape:
					event.preventDefault();
					onEscape?.();
					break;
				case Keys.Home:
					event.preventDefault();
					setActiveIndex(0);
					break;
				case Keys.End:
					event.preventDefault();
					setActiveIndex(itemCount - 1);
					break;
			}
		}, [
			itemCount,
			loop,
			activeIndex,
			onSelect,
			onEscape
		])
	};
}
/**
* Auto-scroll to keep element in view
*/
function useAutoScroll(containerRef, activeIndex) {
	useEffect(() => {
		if (!containerRef.current || activeIndex < 0) return;
		const container = containerRef.current;
		const activeElement = container.children[activeIndex];
		if (!activeElement) return;
		const containerRect = container.getBoundingClientRect();
		const elementRect = activeElement.getBoundingClientRect();
		if (elementRect.bottom > containerRect.bottom) activeElement.scrollIntoView({ block: "nearest" });
		else if (elementRect.top < containerRect.top) activeElement.scrollIntoView({ block: "nearest" });
	}, [containerRef, activeIndex]);
}
/**
* Debounced value
*/
function useDebounce(value, delay) {
	const [debouncedValue, setDebouncedValue] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);
	return debouncedValue;
}
/**
* Media query hook
*/
function useMediaQuery(query) {
	const [matches, setMatches] = useState(false);
	useEffect(() => {
		const media = window.matchMedia(query);
		setMatches(media.matches);
		const listener = (event) => setMatches(event.matches);
		media.addEventListener("change", listener);
		return () => media.removeEventListener("change", listener);
	}, [query]);
	return matches;
}
/**
* Prefers reduced motion
*/
function usePrefersReducedMotion() {
	return useMediaQuery("(prefers-reduced-motion: reduce)");
}
//#endregion
export { useEscapeKey as a, useKeyboardNavigation as c, useScrollLock as d, useHaptic as f, useCanvasViewport as g, useCollaboration as h, useDebounce as i, useMediaQuery as l, useDocSync as m, useClickOutside as n, useFocusTrap as o, useVisualViewport as p, useControllableState as r, useId$1 as s, useAutoScroll as t, usePrefersReducedMotion as u };

//# sourceMappingURL=hooks-ChkgkdFK.mjs.map